import closeCompose from './closeCompose';
import floatCompose from './floatCompose';
import pendCompose from './pendCompose';
import removeInfoBarMessage from './removeInfoBarMessage';
import setHandledByDelaySend from './setHandledByDelaySend';
import moveComposeToTab from './moveComposeToTab';
import composeStore from '../store/composeStore';
import FloatingViewState, { FloatingType } from '../store/schema/FloatingViewState';
import { sendAndSaveInfobarIdsToRemove } from '../utils/InfoBarMessageId';
import endSession from 'owa-controls-findpeople-feedback-manager/lib/actions/endSession';
import { ObservableMap } from 'mobx';
import { findTabByData, lazySetTabIsShown, TabViewState } from 'owa-tab-store';
import isTabViewEnabled from 'owa-tab-store/lib/utils/isTabViewEnabled';
import { action, select } from 'satcheljs/lib/legacy';
import ComposeViewState, {
    AsyncSendState,
    ComposeOperation,
} from '../store/schema/ComposeViewState';

const ASYNC_SEND_WAITING_TIME = 10000;
const ASYNC_SENT_DISMISS_TIME = 3000;

export interface SetAsyncSendStateState {
    pendingViewState: ComposeViewState;
    floatingViewStates: ObservableMap<string, FloatingViewState>;
}

const setAsyncSendState = action('setAsyncSendState')(
    select<SetAsyncSendStateState>({
        pendingViewState: () => composeStore.viewStates.get(composeStore.pendingComposeId),
        floatingViewStates: () => composeStore.floatingViewStates,
    })(function(
        viewState: ComposeViewState,
        asyncSendState: AsyncSendState,
        state?: SetAsyncSendStateState
    ) {
        if (viewState.changeAsyncSendStateTimer) {
            window.clearTimeout(viewState.changeAsyncSendStateTimer);
            viewState.changeAsyncSendStateTimer = 0;
        }

        viewState.asyncSendState = asyncSendState;

        if (isTabViewEnabled()) {
            const tab = findTabByData(viewState.composeId);

            switch (asyncSendState) {
                case AsyncSendState.None:
                    setHandledByDelaySend(viewState, false /* handledByDelaySend */);
                    break;

                case AsyncSendState.Delay:
                    handleDelayOrSendState(viewState, tab);
                    setHandledByDelaySend(viewState, true /* handledByDelaySend */);
                    break;

                case AsyncSendState.Sending:
                    if (!viewState.handledByDelaySend) {
                        handleDelayOrSendState(viewState, tab);
                    }
                    viewState.changeAsyncSendStateTimer = window.setTimeout(() => {
                        setAsyncSendState(viewState, AsyncSendState.Timeout);
                    }, ASYNC_SEND_WAITING_TIME);
                    break;

                case AsyncSendState.Sent:
                    removeInfoBarMessage(viewState, sendAndSaveInfobarIdsToRemove);
                    viewState.changeAsyncSendStateTimer = window.setTimeout(() => {
                        setAsyncSendState(viewState, AsyncSendState.Closed);
                    }, ASYNC_SENT_DISMISS_TIME);
                    break;

                case AsyncSendState.Closed:
                    endSession(viewState.findPeopleFeedbackManager, 'Send');
                    closeCompose(viewState);
                    break;

                case AsyncSendState.Timeout:
                    if (tab) {
                        lazySetTabIsShown.importAndExecute(tab, true /*isShown*/);
                    }
                    break;
            }
        } else {
            // TODO: remove this part once we fully enable tab View
            switch (asyncSendState) {
                case AsyncSendState.None:
                    setHandledByDelaySend(viewState, false /* handledByDelaySend */);
                    break;

                case AsyncSendState.Delay:
                    handleDelayOrSendState(viewState);
                    setHandledByDelaySend(viewState, true /* handledByDelaySend */);
                    break;

                case AsyncSendState.Sending:
                    if (!viewState.handledByDelaySend) {
                        handleDelayOrSendState(viewState);
                    }
                    viewState.changeAsyncSendStateTimer = window.setTimeout(() => {
                        setAsyncSendState(viewState, AsyncSendState.Timeout);
                    }, ASYNC_SEND_WAITING_TIME);
                    break;

                case AsyncSendState.Sent:
                    removeInfoBarMessage(viewState, sendAndSaveInfobarIdsToRemove);
                    if (state.pendingViewState == viewState) {
                        // If we haven't show the toast yet, just let it close silently
                        setAsyncSendState(viewState, AsyncSendState.Closed);
                    } else {
                        // Otherwise, show message sent toast and dismiss in 3s if it is still minimized
                        const floatingViewState = state.floatingViewStates.get(viewState.composeId);

                        if (floatingViewState && floatingViewState.type == FloatingType.Minimized) {
                            viewState.changeAsyncSendStateTimer = window.setTimeout(() => {
                                setAsyncSendState(viewState, AsyncSendState.Closed);
                            }, ASYNC_SENT_DISMISS_TIME);
                        }
                    }
                    break;

                case AsyncSendState.Closed:
                    closeCompose(viewState);
                    break;

                case AsyncSendState.Timeout:
                    floatCompose(viewState, FloatingType.Minimized);
                    break;
            }
        }
    })
);

function handleDelayOrSendState(viewState: ComposeViewState, tab?: TabViewState) {
    if (isTabViewEnabled()) {
        if (viewState.operation === ComposeOperation.EditDraft) {
            moveComposeToTab(viewState, false /* isShown */, false /* makeActive */);
        }
        if (tab) {
            lazySetTabIsShown.importAndExecute(tab, false /*isShown*/);
        }
    } else {
        pendCompose();
    }
}

export default setAsyncSendState;
